import { useCallback } from 'react';
import { getEditorState } from '~/shell/Store';

// Shared module-level state so every hook instance (one per card) sees the same drag context.
let dragSourceId: string | null = null;
let lastHoverId: string | null = null; // last hovered card id used for preview
let scheduled = false; // rAF scheduling flag
let pendingHoverId: string | null = null; // last hover we want to process
// Ghost/visual drag state (module-level so all hook instances share)
let ghostEl: HTMLElement | null = null;
let originEl: HTMLElement | null = null;
let pointerOffsetX = 0;
let pointerOffsetY = 0;
let pendingX: number | null = null;
let pendingY: number | null = null;
let moving = false;
let pointerTracking = false;
let pointerMoveListener: ((e: PointerEvent) => void) | null = null;
let stylesInjected = false;
let docDragOverListener: ((e: DragEvent) => void) | null = null;
let docDragListener: ((e: DragEvent) => void) | null = null;

function injectDragStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'card-drag-optimizations';
  style.textContent = `
  .card-drag-active [data-card-id] { will-change: transform; }
  .card-drag-active .drag-card-ghost { will-change: transform; }
  [data-swap-preview] { pointer-events: none !important; }
  `;
  if (document.head) {
    document.head.appendChild(style);
  }
  stylesInjected = true;
}
// Layout cache for preview animations
const layoutCache: Record<string, DOMRect> = {};
let sourceOriginalRect: DOMRect | null = null;

function cacheLayouts() {
  layoutCache['__time'] = new DOMRect(); // dummy to keep non-empty
  document.querySelectorAll('[data-card-id]').forEach(n => {
    const el = n as HTMLElement;
    const id = el.getAttribute('data-card-id');
    if (!id) return;
    layoutCache[id] = el.getBoundingClientRect();
  });
}

function resetPreviewTransform(id: string | null) {
  if (!id) return;
  const el = document.querySelector(
    `[data-card-id="${id}"]`
  ) as HTMLElement | null;
  if (!el) return;
  el.style.transition = 'transform 160ms cubic-bezier(.2,.8,.2,1)';
  el.style.transform = 'translate(0,0)';
  el.removeAttribute('data-swap-preview');
}

function applyHoverPreview(targetId: string) {
  if (!sourceOriginalRect) return;
  const el = document.querySelector(
    `[data-card-id="${targetId}"]`
  ) as HTMLElement | null;
  if (!el) return;
  const targetRect = layoutCache[targetId] || el.getBoundingClientRect();
  const dx = sourceOriginalRect.left - targetRect.left;
  const dy = sourceOriginalRect.top - targetRect.top;
  el.style.transition = 'transform 180ms cubic-bezier(.2,.8,.2,1)';
  el.style.transform = `translate(${dx}px, ${dy}px)`;
  el.setAttribute('data-swap-preview', 'true');
}

function ensureRafMove() {
  if (moving) return;
  moving = true;
  requestAnimationFrame(() => {
    if (ghostEl && pendingX != null && pendingY != null) {
      ghostEl.style.transform = `translate3d(${pendingX - pointerOffsetX}px, ${
        pendingY - pointerOffsetY
      }px, 0) scale(1.05) rotate(1deg)`;
    }
    moving = false;
    if (pendingX != null || pendingY != null) {
      // If new values were queued while we rendered, schedule again
      if (!moving) ensureRafMove();
    }
  });
}
if (!docDragOverListener) {
  docDragOverListener = e => {
    if (!ghostEl) return;
    if (e.clientX && e.clientY) {
      updateGhostPosition(e.clientX, e.clientY);
    }
    e.preventDefault();
  };
  document.addEventListener('dragover', docDragOverListener, {
    passive: false
  });
}
if (!docDragListener) {
  docDragListener = e => {
    if (!ghostEl) return;
    if (e.clientX && e.clientY) updateGhostPosition(e.clientX, e.clientY);
    // No preventDefault here; just tracking.
  };
  document.addEventListener('drag', docDragListener, { passive: true });
}

function createGhost(source: HTMLElement, clientX: number, clientY: number) {
  removeGhost();
  originEl = source;
  const rect = source.getBoundingClientRect();
  const clone = source.cloneNode(true) as HTMLElement;
  clone.removeAttribute('id');
  if (docDragOverListener) {
    document.removeEventListener('dragover', docDragOverListener);
    docDragOverListener = null;
  }
  if (docDragListener) {
    document.removeEventListener('drag', docDragListener);
    docDragListener = null;
  }
  clone.style.pointerEvents = 'none';
  clone.style.position = 'fixed';
  clone.style.top = '0';
  clone.style.left = '0';
  clone.style.width = rect.width + 'px';
  clone.style.height = rect.height + 'px';
  clone.style.margin = '0';
  clone.style.zIndex = '9999';
  clone.style.boxShadow = '0 8px 24px rgba(0,0,0,0.25)';
  clone.style.borderRadius = getComputedStyle(source).borderRadius || '20px';
  clone.style.background = getComputedStyle(source).backgroundColor || 'white';
  clone.style.transition = 'box-shadow .18s cubic-bezier(.2,.8,.2,1)';
  clone.style.willChange = 'transform';
  clone.style.transform = `translate3d(${rect.left}px, ${rect.top}px,0) scale(.95)`;
  clone.classList.add('drag-card-ghost');
  if (document.body) {
    document.body.appendChild(clone);
  }
  ghostEl = clone;
  pointerOffsetX = clientX - rect.left;
  pointerOffsetY = clientY - rect.top;
  requestAnimationFrame(() => {
    if (ghostEl) {
      ghostEl.style.transform = `translate3d(${clientX - pointerOffsetX}px, ${
        clientY - pointerOffsetY
      }px,0) scale(1.05) rotate(1deg)`;
      ghostEl.style.opacity = '0.98';
    }
  });
  source.style.opacity = '0.35';
  source.style.transition = 'opacity .15s';
  source.setAttribute('data-drag-origin', 'true');
  document.body.classList.add('card-drag-active');
}

function updateGhostPosition(clientX: number, clientY: number) {
  if (!ghostEl) return;
  pendingX = clientX;
  pendingY = clientY;
  ensureRafMove();
}

function removeGhost() {
  if (ghostEl && ghostEl.parentNode) ghostEl.parentNode.removeChild(ghostEl);
  ghostEl = null;
  pendingX = pendingY = null;
  if (originEl) {
    originEl.style.opacity = '';
    originEl.removeAttribute('data-drag-origin');
    originEl = null;
  }
  document.body.classList.remove('card-drag-active');
}

export function useReorderCards() {
  const isCard = (i: any) =>
    i?.typename === 'Card' || i?.__typename === 'Card' || i?.type === 'CARD';

  const getCardsContext = () => {
    const { sequence, contentEditable } = getEditorState();
    if (!sequence || !contentEditable) return null; // only active in editor mode
    const rootItems = sequence.items || [];
    const cards = rootItems.filter((i: any) => isCard(i));
    return { sequence, rootItems, cards, contentEditable };
  };

  const applyNewOrder = (cards: any[], rootItems: any[]) => {
    const { setSequence, sequence } = getEditorState();
    const cardIndices: number[] = [];
    rootItems.forEach((item: any, idx: number) => {
      if (isCard(item)) cardIndices.push(idx);
    });
    const ordered = [...rootItems];
    cards
      .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
      .forEach((card: any, i: number) => {
        const targetIndex = cardIndices[i];
        if (targetIndex !== undefined) ordered[targetIndex] = card;
      });
    setSequence({ ...sequence, items: ordered });
  };

  const onDragStart = useCallback(
    (id: string, e?: React.DragEvent | React.MouseEvent) => {
      const { contentEditable } = getEditorState();
      if (!contentEditable) return;
      if ((e as any)?.nativeEvent?.type === 'pointerdown') return;
      dragSourceId = id;
      lastHoverId = null;
      cacheLayouts();
      const sourceEl = document.querySelector(
        `[data-card-id="${id}"]`
      ) as HTMLElement | null;
      sourceOriginalRect = sourceEl ? sourceEl.getBoundingClientRect() : null;
      injectDragStyles();
      if (!pointerTracking) {
        pointerTracking = true;
        pointerMoveListener = e => {
          if (ghostEl) {
            pendingX = e.clientX;
            pendingY = e.clientY;
            ensureRafMove();
          }
        };
        window.addEventListener('pointermove', pointerMoveListener, {
          passive: true
        });
      }

      const evt = e as React.DragEvent | undefined;
      const targetEl = (evt?.currentTarget as HTMLElement) || null;
      if (targetEl && evt && 'clientX' in evt) {
        createGhost(targetEl, (evt as any).clientX, (evt as any).clientY);
      }
    },
    []
  );

  const previewMove = useCallback((hoverId: string) => {
    const { contentEditable } = getEditorState();
    if (!contentEditable) return;
    if (!dragSourceId || dragSourceId === hoverId) return;
    if (lastHoverId === hoverId) return; // already previewing
    // Reset previous preview
    resetPreviewTransform(lastHoverId);
    lastHoverId = hoverId;
    pendingHoverId = hoverId;
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      const target = pendingHoverId;
      pendingHoverId = null;
      if (!target || !dragSourceId) return;
      applyHoverPreview(target);
    });
  }, []);

  const onDragEnter = useCallback(
    (targetId: string) => {
      const { contentEditable } = getEditorState();
      if (!contentEditable) return;
      previewMove(targetId);
    },
    [previewMove]
  );

  const finalize = useCallback(() => {
    resetPreviewTransform(lastHoverId);
    dragSourceId = null;
    lastHoverId = null;
    sourceOriginalRect = null;
    removeGhost();
    if (pointerTracking && pointerMoveListener) {
      window.removeEventListener('pointermove', pointerMoveListener);
      pointerTracking = false;
      pointerMoveListener = null;
    }
  }, []);

  const onDrop = useCallback(() => {
    const { contentEditable } = getEditorState();
    if (!contentEditable) return;
    if (dragSourceId && lastHoverId && dragSourceId !== lastHoverId) {
      // Perform actual swap in data model
      const ctx = getCardsContext();
      if (ctx) {
        const { cards, rootItems } = ctx;
        const aIndex = cards.findIndex(
          (c: any) => String(c.id) === dragSourceId
        );
        const bIndex = cards.findIndex(
          (c: any) => String(c.id) === lastHoverId
        );
        if (aIndex > -1 && bIndex > -1) {
          const updated = [...cards];
          const tempPos = updated[aIndex].position;
          updated[aIndex].position = updated[bIndex].position;
          updated[bIndex].position = tempPos;
          applyNewOrder(updated, rootItems);
        }
      }
    }
    finalize();
  }, [finalize]);

  const onDragEnd = useCallback(() => {
    const { contentEditable } = getEditorState();
    if (!contentEditable) return;
    finalize();
  }, [finalize]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    const { contentEditable } = getEditorState();
    if (!contentEditable) return;
    e.preventDefault();
    if (e.clientX && e.clientY) {
      updateGhostPosition(e.clientX, e.clientY);
    }
  }, []);

  let pointerActive = false;
  let pointerSourceId: string | null = null;
  let lastHoverCandidate: string | null = null;

  const endPointerDrag = async (commit: boolean) => {
    if (!pointerActive) return;
    pointerActive = false;
    if (
      commit &&
      pointerSourceId &&
      lastHoverCandidate &&
      lastHoverCandidate !== pointerSourceId
    ) {
      const { apiService } = getEditorState();
      // Perform swap
      const ctx = getCardsContext();
      if (ctx) {
        const { cards, rootItems } = ctx;
        const aIndex = cards.findIndex(
          (c: any) => String(c.id) === pointerSourceId
        );
        const bIndex = cards.findIndex(
          (c: any) => String(c.id) === lastHoverCandidate
        );
        if (aIndex > -1 && bIndex > -1) {
          const updated = [...cards];
          const tempPos = updated[aIndex].position;
          updated[aIndex].position = updated[bIndex].position;
          updated[bIndex].position = tempPos;
          applyNewOrder(updated, rootItems);

          if (apiService) {
            await Promise.all(
              updated.map(card =>
                apiService.updateSubItem(card.id, 'position', card.position)
              )
            );
          }
        }
      }
    }
    resetPreviewTransform(lastHoverId);
    lastHoverId = null;
    pointerSourceId = null;
    sourceOriginalRect = null;
    removeGhost();
    if (pointerMoveListener) {
      window.removeEventListener('pointermove', pointerMoveListener);
      pointerMoveListener = null;
      pointerTracking = false;
    }
    if (docDragOverListener) {
      document.removeEventListener('dragover', docDragOverListener);
      docDragOverListener = null;
    }
    if (docDragListener) {
      document.removeEventListener('drag', docDragListener);
      docDragListener = null;
    }
    window.removeEventListener('pointerup', pointerUpHandler);
    document.body.classList.remove('card-drag-active');
  };

  const pointerUpHandler = async (e: PointerEvent) => {
    await endPointerDrag(true);
  };

  const onPointerDown = (id: string, e: React.PointerEvent) => {
    const { contentEditable } = getEditorState();
    if (!contentEditable) return;
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (
      target.closest(
        'button, a, [role="button"], input, textarea, [contenteditable="true"], .no-drag'
      )
    ) {
      return; // allow normal interaction
    }
    const startX = e.clientX;
    const startY = e.clientY;
    let hasMovedThreshold = false;
    e.preventDefault();
    pointerActive = true;
    pointerSourceId = id;
    dragSourceId = id;
    lastHoverId = null;
    cacheLayouts();

    const sourceEl = document.querySelector(
      `[data-card-id="${id}"]`
    ) as HTMLElement | null;

    sourceOriginalRect = sourceEl ? sourceEl.getBoundingClientRect() : null;
    injectDragStyles();
    if (sourceEl) {
      createGhost(sourceEl, e.clientX, e.clientY);
    }
    // Track pointer manually
    pointerMoveListener = (pe: any) => {
      if (!pointerActive) return;
      if (!hasMovedThreshold) {
        const dx = Math.abs(pe.clientX - startX);
        const dy = Math.abs(pe.clientY - startY);
        if (dx + dy > 6) {
          hasMovedThreshold = true;
        } else {
          return; // ignore tiny moves until threshold reached
        }
      }
      pendingX = pe.clientX;
      pendingY = pe.clientY;
      ensureRafMove();
      // Determine hovered card
      const el = document.elementFromPoint(
        pe.clientX,
        pe.clientY
      ) as HTMLElement | null;
      if (!el) return;
      const cardEl = el.closest('[data-card-id]') as HTMLElement | null;
      if (cardEl) {
        const hoverId = cardEl.getAttribute('data-card-id');
        if (hoverId && hoverId !== pointerSourceId) {
          if (hoverId !== lastHoverId) {
            resetPreviewTransform(lastHoverId);
            lastHoverId = hoverId;
            applyHoverPreview(hoverId);
            lastHoverCandidate = hoverId;
          }
        } else if (hoverId === pointerSourceId) {
          // Return previewed card if moving back over source
          resetPreviewTransform(lastHoverId);
          lastHoverId = null;
          lastHoverCandidate = null;
        }
      }
    };
    window.addEventListener('pointermove', pointerMoveListener, {
      passive: true
    });
    window.addEventListener('pointerup', pointerUpHandler, {
      passive: true,
      once: true
    });
  };

  return {
    onDragStart,
    onDragEnter,
    onDrop,
    onDragOver,
    onDragEnd,
    onPointerDown
  };
}
